import React, { Component } from 'react';
import { Dropdown, Nav } from 'react-bootstrap';
import { GoSun, GoMoon, GoDeviceDesktop, GoCheck } from 'react-icons/go';
import Theme from 'context/theme';
import './index.css';

// `system` first: it is the default, so it is the row most people are already on and the one they
// come back to. The two explicit choices follow it, light before dark.
const CHOICES = [
  { value: "system", label: "System", Icon: GoDeviceDesktop },
  { value: "light",  label: "Light",  Icon: GoSun },
  { value: "dark",   label: "Dark",   Icon: GoMoon }
];

/**
 * The site theme control: a glyph in the navbar that opens Light / Dark / System.
 *
 * Shape is UserMenu's — react-bootstrap Dropdown, a `.nav-link` toggle, a right-aligned
 * `.dropdown-menu` — with one level of nesting NavDropdown normally hides. NavDropdown spreads its
 * own props onto the wrapping <Dropdown>, not onto the toggle, so `aria-label` written there lands
 * on a <div> and names nothing. Writing `Dropdown` + `Dropdown.Toggle as={Nav.Link}` is exactly what
 * NavDropdown does internally (react-bootstrap/cjs/NavDropdown.js:41-51) and is the only way to put
 * an accessible name on the anchor the user actually focuses.
 *
 * The glyph shows the RESOLVED theme, sun or moon, matching Mintlify: what you are LOOKING AT, not
 * what you last PICKED. Showing the choice (a desktop-monitor glyph for "System") is what shipped
 * first and it read as unexplained noise sitting between Docs and the account avatar — a
 * sun/moon-only toggle cannot show three states, so it does not try to. "Which setting am I on" is
 * answered by opening the menu, where the check gutter marks the actual choice; `aria-label` states
 * both facts for anyone who cannot see the glyph at all.
 */
export default class ThemeMenu extends Component {
  constructor(props) {
    super(props);
    // Synchronous read. The store resolves both before first paint (that is its contract), so the
    // control is never briefly wrong the way a componentDidMount read would make it.
    this.state = { choice: Theme.choice(), resolved: Theme.resolved() };
  }

  componentDidMount() {
    // Same subscribe/unsubscribe lifecycle BlobHeaderToggle uses against Preferences, so the two
    // navbar controls that watch a stored value watch it the same way. Both the choice AND the
    // resolution are re-read: the OS can flip while the choice stays "system", which moves the
    // glyph without moving the check gutter, and Theme.apply() notifies for exactly that case.
    this.unsubscribe = Theme.subscribe(() => {
      const choice = Theme.choice();
      const resolved = Theme.resolved();
      if (choice !== this.state.choice || resolved !== this.state.resolved) {
        this.setState({ choice: choice, resolved: resolved });
      }
    });
  }

  componentWillUnmount() {
    if (this.unsubscribe) this.unsubscribe();
  }

  select = (value) => {
    Theme.set(value);
  }

  render() {
    const choiceEntry = CHOICES.find((c) => c.value === this.state.choice) || CHOICES[2];
    const shownEntry = CHOICES.find((c) => c.value === this.state.resolved) || CHOICES[0];
    const Glyph = shownEntry.Icon;
    // Off "system" the resolved theme IS the choice, so this only ever adds information — it never
    // contradicts the plain "Theme: <label>" form used the rest of the time.
    const ariaLabel = "system" === this.state.choice
      ? `Theme: ${shownEntry.label} (System)`
      : `Theme: ${choiceEntry.label}`;

    return (
      <Dropdown className="nav-item bh-theme-menu" alignRight>
        <Dropdown.Toggle
          as={Nav.Link}
          id="theme-nav-dropdown"
          className="bh-theme-toggle"
          aria-haspopup="menu"
          aria-label={ariaLabel}
        >
          <Glyph/>
        </Dropdown.Toggle>
        {/* role="menu" is not decoration: react-overlays only auto-focuses the first item on a
            keyboard open when the menu matches [role=menu] (react-overlays/cjs/Dropdown.js:183).
            Without it, opening with Enter leaves focus on the toggle and Down-arrow is the only way
            in. UserMenu has that gap today; this control does not inherit it. */}
        <Dropdown.Menu role="menu" className="bh-theme-menu-panel">
          {CHOICES.map(({ value, label, Icon }) => (
            <Dropdown.Item
              key={value}
              role="menuitemradio"
              aria-checked={value === this.state.choice}
              className="bh-theme-item"
              onClick={() => this.select(value)}
            >
              {/* A reserved check gutter, not Bootstrap's `.dropdown-item.active`: that paints a
                  solid #007bff bar across the row, which is far heavier than "this is the current
                  one" and would be the only saturated block in the menu. The gutter is always the
                  same width, so the three labels stay on one left edge whichever is checked. */}
              <span className="bh-theme-check">{value === this.state.choice && <GoCheck/>}</span>
              <Icon className="bh-theme-item-icon"/>
              <span className="bh-theme-item-label">{label}</span>
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    );
  }
}
